var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ButtonList, DropdownButton, IconButton, Page, Table, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { produce } from 'immer';
import { useCallback, useMemo, useRef, useState } from 'react';
import { customFieldsApi } from 'apis/customFields';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { getAsyncStatusFromRtkQueryStates } from 'utils/apis/axios';
import { categories } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { CustomAttributeModal } from './components/modal/customAttributeModal';
import { CustomAttributesTableRow } from './components/row/customAttributesTableRow';
import styles from './customAttributesTable.module.scss';
const filterOptions = {
    all: 'All attributes',
    active: 'Active attributes',
    inactive: 'Inactive attributes',
};
const orderIndexStep = Math.pow(2, 24);
/** Represents the settings custom attributes table. */
export const CustomAttributesTable = ({ entityId, }) => {
    var _a, _b, _c, _d;
    const dispatch = useAppDispatch();
    const response = customFieldsApi.useReadAttributesQuery({
        entityId,
    });
    const asyncStatus = getAsyncStatusFromRtkQueryStates(response);
    const [filterMode, setFilterMode] = useState('active');
    const recentlyToggledAttributes = useRef(new Set([]));
    const orderedAttributes = useMemo(() => {
        var _a, _b;
        return [...((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data.attributes) !== null && _b !== void 0 ? _b : [])].filter((attribute) => {
            if (recentlyToggledAttributes.current.has(attribute.id)) {
                return true;
            }
            switch (filterMode) {
                case 'all':
                    return true;
                case 'inactive':
                    return attribute.is_deactivated === true;
                default:
                    return attribute.is_deactivated === false;
            }
        });
    }, [filterMode, (_a = response.data) === null || _a === void 0 ? void 0 : _a.data.attributes]);
    const handleAttributeActiveStateChange = useCallback((attributeId) => {
        recentlyToggledAttributes.current.add(attributeId);
    }, []);
    /** Handles opening the creation modal. */
    const handleCreateCustomAttribute = useCallback(() => {
        var _a, _b;
        dispatch(appActions.showModal({
            modal: {
                type: 'settings:customAttribute',
                arg: {
                    entityId,
                    mode: 'create',
                    attributes: (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data.attributes) !== null && _b !== void 0 ? _b : [],
                },
            },
        }));
        analytics.settingsCustomAttributeAddNewAttributeClicked({ entityId });
    }, [dispatch, entityId, response]);
    /** Handles the event for when an attribute is moved. */
    const handleAttributeMove = ({ dragIndex, hoverIndex, }) => __awaiter(void 0, void 0, void 0, function* () {
        var _e, _f;
        const newIndex = hoverIndex + (dragIndex < hoverIndex ? 1 : -1);
        const newOrderIndex = (((_f = (_e = orderedAttributes[newIndex]) === null || _e === void 0 ? void 0 : _e.order_index) !== null && _f !== void 0 ? _f : 0) +
            orderedAttributes[hoverIndex].order_index) /
            2;
        if (Math.abs(newOrderIndex - orderedAttributes[hoverIndex].order_index) > 1) {
            const originalAttribute = orderedAttributes[dragIndex];
            const updatedAttribute = produce(originalAttribute, (draft) => {
                draft.order_index = Math.floor(newOrderIndex);
            });
            const attributeId = originalAttribute.id;
            yield dispatch(customFieldsApi.endpoints.updateAttribute.initiate({
                attributeId,
                entityId,
                patch: {
                    before: originalAttribute,
                    after: updatedAttribute,
                },
            }));
            analytics.settingsCustomAttributeReordered({
                attributeId,
                entityId,
                isFullRecompute: false,
            });
        }
        else {
            const tempAttributes = produce(orderedAttributes, (draft) => {
                draft[dragIndex].order_index = newOrderIndex;
                draft.sort((a, b) => a.order_index - b.order_index);
            });
            for (let i = tempAttributes.length; i > 0; i--) {
                const originalAttribute = tempAttributes[i - 1];
                const attributeId = originalAttribute.id;
                yield dispatch(customFieldsApi.endpoints.updateAttribute.initiate({
                    attributeId,
                    entityId,
                    patch: {
                        before: originalAttribute,
                        after: produce(originalAttribute, (draft) => {
                            draft.order_index = i * orderIndexStep;
                        }),
                    },
                }));
                analytics.settingsCustomAttributeReordered({
                    attributeId,
                    entityId,
                    isFullRecompute: true,
                });
            }
        }
    });
    const showFeedbackModal = useCallback(() => {
        dispatch(appActions.showModal({
            modal: {
                type: 'app:feedbackForm',
                arg: {
                    team: 'conversations',
                    title: 'Custom attributes feedback',
                    extra: {
                        entityId,
                    },
                },
            },
        }));
    }, [dispatch, entityId]);
    const handleFilterModeChange = useCallback((key) => {
        recentlyToggledAttributes.current.clear();
        setFilterMode(key);
    }, []);
    const controls = (_jsxs(_Fragment, { children: [_jsx(DropdownButton, Object.assign({ dropdown: _jsx(ButtonList, { children: Object.keys(filterOptions).map((key) => (_jsx(ButtonList.Item, Object.assign({ isSelected: filterMode === key, onClick: () => handleFilterModeChange(key) }, { children: filterOptions[key] }), key))) }) }, { children: filterOptions[filterMode] })), _jsx(Button, Object.assign({ onClick: handleCreateCustomAttribute }, { children: "Add custom attribute" }))] }));
    const table = (_jsxs(Table, Object.assign({ stickyHeader: true, loading: asyncStatus === 'pending', loadingFailed: asyncStatus === 'rejected', emptyState: !((_d = (_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.attributes) === null || _d === void 0 ? void 0 : _d.length)
            ? {
                title: 'No custom attributes found',
                description: 'Get started by adding your first custom attribute.',
            }
            : undefined }, { children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, { children: "Name and description" }), _jsx(Table.Th, Object.assign({ width: "15%" }, { children: "Identifier" })), _jsx(Table.Th, Object.assign({ width: "0" }, { children: "Active" })), _jsx(Table.Th, {})] }) }), _jsx(Table.Tbody, { children: orderedAttributes.map((attribute, index) => (_jsx(CustomAttributesTableRow, { attribute: attribute, index: index, onMove: handleAttributeMove, onActiveStateChange: handleAttributeActiveStateChange }, attribute.id))) })] })));
    return (_jsxs(_Fragment, { children: [_jsx(Page.Section, Object.assign({ title: `Custom ${entityId} attributes`, titleMeta: _jsxs(_Fragment, { children: [_jsx(Tooltip, Object.assign({ content: "Help and documentation", dark: true }, { children: _jsx(IconButton, { icon: "question", variant: "tertiary", onClick: () => elevioClient.openCategory(categories.settings.customAttributes.documentation) }) })), _jsx("span", Object.assign({ onClick: () => showFeedbackModal() }, { children: _jsx(Text.Paragraph, Object.assign({ as: "span", variant: "4", className: styles.feedbackLink }, { children: "Send feedback" })) }))] }), controls: controls }, { children: table })), _jsx(CustomAttributeModal, {})] }));
};
